import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { PasswordRequirements } from './PasswordRequirements';
import { isPasswordValid, PASSWORD_POLICY_TEXT } from '../lib/passwordPolicy';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  User,
  Building,
  Loader2,
  Lock,
  Eye,
  EyeOff,
  Key,
  Link2,
  Copy,
  Trash,
  QrCode,
  Download,
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Camera } from 'lucide-react';

const getPhotoInitials = (name) => (name || '?').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

const COUNTRIES = [
  'Australia', 'New Zealand', 'United States', 'United Kingdom', 
  'Canada', 'Singapore', 'Malaysia', 'Indonesia', 'Philippines',
  'India', 'Japan', 'South Korea', 'Germany', 'France', 'Other'
];

export const ProfileModal = ({ open, onOpenChange, onPhotoChanged }) => {
  const { api, user: currentUser, setUser } = useAuth();
  const [activeTab, setActiveTab] = useState('account');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [quickLoginLoading, setQuickLoginLoading] = useState(false);
  const [quickLoginToken, setQuickLoginToken] = useState(null);
  const [showQuickLoginQR, setShowQuickLoginQR] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoSource, setPhotoSource] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);

  const loadPhoto = async () => {
    try {
      const r = await api.get('/me/person-photo', { responseType: 'blob' });
      setPhotoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(r.data); });
      setPhotoSource(r.headers['x-photo-source'] || null);
    } catch {
      setPhotoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
      setPhotoSource(null);
    }
  };

  const handlePhotoUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/me/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadPhoto();
      onPhotoChanged?.();
      toast.success('Profile photo updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to upload photo');
    } finally { setPhotoBusy(false); }
  };

  const handlePhotoRemove = async () => {
    setPhotoBusy(true);
    try {
      await api.delete('/me/photo');
      await loadPhoto();
      onPhotoChanged?.();
      toast.success('Profile photo removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove photo');
    } finally { setPhotoBusy(false); }
  };
  
  const [formData, setFormData] = useState({
    full_name: '',
    company: '',
    address_street: '',
    address_city: '',
    address_postcode: '',
    address_state: '',
    address_country: 'Australia',
    phone: '',
    mobile: '',
    self_display_name_preference: 'personal',
  });

  // Load user data when modal opens
  useEffect(() => {
    if (open && currentUser) {
      loadUserData();
      loadPhoto();
    }
  // Load profile data when the dialog opens. loadUserData is an inline fetch
  // recreated each render; intentionally keyed on [open, currentUser] only so it
  // runs on open rather than refetching on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentUser]);

  const loadUserData = async () => {
    if (!currentUser?.id) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/users/${currentUser.id}`);
      const userData = response.data;
      
      // Set quick login token if exists
      setQuickLoginToken(userData.quick_login_token || null);
      setShowQuickLoginQR(false);
      
      setFormData({
        full_name: userData.full_name || '',
        company: userData.company || '',
        address_street: userData.address_street || '',
        address_city: userData.address_city || '',
        address_postcode: userData.address_postcode || '',
        address_state: userData.address_state || '',
        address_country: userData.address_country || 'Australia',
        phone: userData.phone || '',
        mobile: userData.mobile || '',
        self_display_name_preference: userData.self_display_name_preference || 'personal',
      });
    } catch (error) {
      console.error('Failed to load user data:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!currentUser?.id) return;
    
    setSaving(true);
    try {
      const response = await api.put(`/users/${currentUser.id}`, formData);
      
      // Update the user context with the new data so the UI reflects changes immediately
      setUser(prev => ({
        ...prev,
        full_name: formData.full_name,
        company: formData.company,
        phone: formData.phone,
        mobile: formData.mobile,
        address_street: formData.address_street,
        address_city: formData.address_city,
        address_postcode: formData.address_postcode,
        address_state: formData.address_state,
        address_country: formData.address_country,
        self_display_name_preference: formData.self_display_name_preference,
      }));
      
      toast.success('Profile updated successfully');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    
    if (!isPasswordValid(newPassword)) {
      toast.error(PASSWORD_POLICY_TEXT);
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.put(`/users/${currentUser.id}/password`, {
        new_password: newPassword
      });
      toast.success('Password changed successfully. Please log in again.');
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Quick Login Link functions
  const handleGenerateQuickLogin = async () => {
    if (!currentUser?.id) return;
    
    setQuickLoginLoading(true);
    try {
      const response = await api.post(`/users/${currentUser.id}/quick-login-token`);
      const token = response.data.quick_login_token;
      setQuickLoginToken(token);
      toast.success('Quick login link generated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate quick login link');
    } finally {
      setQuickLoginLoading(false);
    }
  };

  const handleRevokeQuickLogin = async () => {
    if (!currentUser?.id) return;
    
    setQuickLoginLoading(true);
    try {
      await api.delete(`/users/${currentUser.id}/quick-login-token`);
      setQuickLoginToken(null);
      toast.success('Quick login link revoked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke quick login link');
    } finally {
      setQuickLoginLoading(false);
    }
  };

  const copyQuickLoginUrl = () => {
    if (!quickLoginToken) return;
    
    // Use frontend route, not API endpoint
    const baseUrl = window.location.origin;
    const quickLoginUrl = `${baseUrl}/quick-login/${quickLoginToken}`;
    
    navigator.clipboard.writeText(quickLoginUrl);
    toast.success('Quick login URL copied to clipboard');
  };

  const tabs = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'contact', label: 'Contact', icon: Building },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>My Profile</DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 border-b">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors
                ${activeTab === tab.id 
                  ? 'text-blue-600 border-blue-600' 
                  : 'text-muted-foreground border-transparent hover:text-foreground'
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {/* Account Tab */}
              {activeTab === 'account' && (
                <div className="space-y-6">
                  {/* Profile Photo */}
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => { if (photoUrl) setPhotoViewerOpen(true); }}
                      title={photoUrl ? 'View photo' : undefined}
                      className={`shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563eb] ${photoUrl ? 'cursor-pointer hover:opacity-90 transition-opacity' : 'cursor-default'}`}
                      data-testid="profile-photo-view-btn"
                    >
                      <Avatar className="w-16 h-16 bg-[#2563eb]">
                        {photoUrl && <AvatarImage src={photoUrl} alt="Profile" className="object-cover" data-testid="profile-photo-preview" />}
                        <AvatarFallback className="bg-[#2563eb] text-white text-lg font-semibold">
                          {getPhotoInitials(currentUser?.full_name)}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <input type="file" id="profile-photo-file" className="hidden" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(e) => { handlePhotoUpload(e.target.files); e.target.value = ''; }} data-testid="profile-photo-file-input" />
                        <Button type="button" variant="outline" size="sm" disabled={photoBusy} onClick={() => document.getElementById('profile-photo-file').click()} data-testid="profile-photo-upload-btn">
                          {photoBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
                          {photoSource === 'account' ? 'Change photo' : 'Upload photo'}
                        </Button>
                        {photoSource === 'account' && (
                          <Button type="button" variant="ghost" size="sm" className="text-red-600 hover:text-red-700" disabled={photoBusy} onClick={handlePhotoRemove} data-testid="profile-photo-remove-btn">
                            <Trash className="w-4 h-4 mr-1" /> Remove
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {photoSource === 'linked' ? 'Currently using the photo from your linked Driver/Staff record — upload your own to override it.' : 'JPEG, PNG, WEBP or GIF, max 5 MB. Shown on your account avatar.'}
                      </p>
                    </div>
                  </div>

                  {/* Decoy fields to prevent Chrome autofill */}
                  <input type="text" name="prevent-autofill-user" style={{ display: 'none' }} autoComplete="username" tabIndex={-1} />
                  <input type="password" name="prevent-autofill-pass" style={{ display: 'none' }} autoComplete="current-password" tabIndex={-1} />
                  
                  {/* Email (Read-only) */}
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={currentUser?.email || ''}
                      disabled
                      className="bg-muted cursor-not-allowed"
                      autoComplete="off"
                    />
                    <p className="text-xs text-muted-foreground">
                      Email can only be changed by an administrator
                    </p>
                  </div>

                  {/* Full Name */}
                  <div className="space-y-2">
                    <Label>Full Name *</Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => handleInputChange('full_name', e.target.value)}
                      placeholder="Enter your full name"
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                    />
                  </div>

                  {/* Change Password Section */}
                  <div className="pt-4 border-t">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Change Password
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Set a new password for your account. You will need to log in again.
                    </p>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter a strong password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <Button
                        variant="outline"
                        onClick={handlePasswordChange}
                        disabled={changingPassword || !isPasswordValid(newPassword)}
                        className="text-blue-600 border-blue-300"
                      >
                        {changingPassword ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Key className="w-4 h-4 mr-2" />
                        )}
                        Change Password
                      </Button>
                    </div>
                    {newPassword && (
                      <PasswordRequirements password={newPassword} className="mt-2" />
                    )}
                  </div>

                  {/* Quick Login Link Section */}
                  <div className="pt-4 border-t">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Link2 className="w-4 h-4" />
                      Quick Login Link
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Generate a temporary URL that allows direct login without a password. Useful for support access or quick sharing.
                    </p>
                    
                    {quickLoginToken ? (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            value={`${window.location.origin}/quick-login/${quickLoginToken}`}
                            readOnly
                            className="font-mono text-xs"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={copyQuickLoginUrl}
                            className="shrink-0"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Copy
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowQuickLoginQR(v => !v)}
                            className={`shrink-0 ${showQuickLoginQR ? 'border-blue-500 text-blue-600' : ''}`}
                            data-testid="profile-quick-login-qr-btn"
                          >
                            <QrCode className="w-4 h-4 mr-2" />
                            QR Code
                          </Button>
                        </div>
                        {showQuickLoginQR && (
                          <div className="flex flex-col items-center gap-3 p-4 rounded-lg border bg-muted/20" data-testid="profile-quick-login-qr-panel">
                            <div id="profile-quick-login-qr-canvas" className="p-3 bg-white rounded-lg border" data-testid="profile-quick-login-qr-code">
                              <QRCodeCanvas value={`${window.location.origin}/quick-login/${quickLoginToken}`} size={200} level="M" />
                            </div>
                            <p className="text-xs text-muted-foreground text-center">Scan this code with a phone camera to log in instantly as you.</p>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const canvas = document.querySelector('#profile-quick-login-qr-canvas canvas');
                                if (!canvas) return;
                                const link = document.createElement('a');
                                link.download = 'my-quick-login-qr.png';
                                link.href = canvas.toDataURL('image/png');
                                link.click();
                              }}
                              data-testid="profile-quick-login-qr-download-btn"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download QR Code
                            </Button>
                          </div>
                        )}
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={handleRevokeQuickLogin}
                          disabled={quickLoginLoading}
                        >
                          {quickLoginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash className="w-4 h-4 mr-2" />}
                          Revoke Quick Login Link
                        </Button>
                        <p className="text-xs text-amber-600">
                          ⚠️ This link allows anyone with it to log in as you. Revoke if no longer needed.
                        </p>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleGenerateQuickLogin}
                        disabled={quickLoginLoading}
                        className="border-blue-500 text-blue-600 hover:bg-blue-50"
                      >
                        {quickLoginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
                        Generate Quick Login Link
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Tab */}
              {activeTab === 'contact' && (
                <div className="space-y-6">
                  {/* Company Name */}
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      value={formData.company}
                      onChange={(e) => {
                        handleInputChange('company', e.target.value);
                        if (!e.target.value.trim() && formData.self_display_name_preference === 'company') {
                          handleInputChange('self_display_name_preference', 'personal');
                        }
                      }}
                      placeholder="Enter company name"
                    />
                  </div>

                  {/* Display As (main accounts only — sub accounts inherit nothing to choose) */}
                  {currentUser?.role !== 'sub_account' && (
                    <div className="space-y-2">
                      <Label>Display As</Label>
                      <Select
                        value={formData.self_display_name_preference || 'personal'}
                        onValueChange={(v) => handleInputChange('self_display_name_preference', v)}
                      >
                        <SelectTrigger data-testid="profile-display-name-preference-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="personal" data-testid="profile-display-name-preference-personal">
                            Personal name{formData.full_name ? ` (${formData.full_name})` : ''}
                          </SelectItem>
                          <SelectItem value="company" disabled={!(formData.company || '').trim()} data-testid="profile-display-name-preference-company">
                            Company name{(formData.company || '').trim() ? ` (${formData.company.trim()})` : ' — set a company first'}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Chooses which name represents your account in your app (header and avatar). Your fleet operator's admin view is managed separately.
                      </p>
                    </div>
                  )}

                  {/* Street Address */}
                  <div className="space-y-2">
                    <Label>Street Address</Label>
                    <Input
                      value={formData.address_street}
                      onChange={(e) => handleInputChange('address_street', e.target.value)}
                      placeholder="123 Main Street"
                    />
                  </div>

                  {/* City, Postcode, State, Country */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input
                        value={formData.address_city}
                        onChange={(e) => handleInputChange('address_city', e.target.value)}
                        placeholder="Sydney"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Postcode</Label>
                      <Input
                        value={formData.address_postcode}
                        onChange={(e) => handleInputChange('address_postcode', e.target.value)}
                        placeholder="2000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Input
                        value={formData.address_state}
                        onChange={(e) => handleInputChange('address_state', e.target.value)}
                        placeholder="NSW"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Country</Label>
                      <Select
                        value={formData.address_country}
                        onValueChange={(value) => handleInputChange('address_country', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map(country => (
                            <SelectItem key={country} value={country}>
                              {country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Phone and Mobile */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="+61400000000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Mobile</Label>
                      <Input
                        value={formData.mobile}
                        onChange={(e) => handleInputChange('mobile', e.target.value)}
                        placeholder="+61 412 345 678"
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>

        {/* Avatar photo viewer — same pattern as the Vehicle Dashboard photo modal */}
        <Dialog open={photoViewerOpen && !!photoUrl} onOpenChange={setPhotoViewerOpen}>
          <DialogContent className="max-w-xl" data-testid="profile-photo-viewer">
            <DialogHeader>
              <DialogTitle>{currentUser?.full_name || 'Profile photo'}</DialogTitle>
            </DialogHeader>
            {photoUrl && <img src={photoUrl} alt={currentUser?.full_name || 'Profile'} className="w-full max-h-[65vh] object-contain rounded-md border" data-testid="profile-photo-viewer-img" />}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};
